/**
 * MandateSituation — STaRS Situation Assessment.
 */

import { useState, useEffect } from 'react';
import { Compass } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { PageHeader } from '../../components/prioritization/PageHeader';
import type { STaRSSituation } from '../../types/mandate';

const SITUATION_OPTIONS: {
  type: STaRSSituation;
  label: string;
  description: string;
}[] = [
  { type: 'startup', label: 'Startup', description: 'Building something new' },
  { type: 'turnaround', label: 'Turnaround', description: 'Fixing something broken' },
  { type: 'realignment', label: 'Realignment', description: 'Redirecting something that works' },
  { type: 'sustaining', label: 'Sustaining Success', description: 'Growing something strong' },
];

const STARS_INTELLIGENCE: Record<
  STaRSSituation,
  { mood: string; yourJob: string; avoid: string }
> = {
  startup: {
    mood:
      "The team is often energized but uncertain — they're looking to you to establish direction and norms fast.",
    yourJob:
      "Build the foundation: define what winning looks like, staff the team, and create early wins that establish credibility.",
    avoid:
      "Avoid premature scaling and over-planning. The biggest risk is building the wrong thing with too much confidence.",
  },
  turnaround: {
    mood:
      "The team is often demoralized or in denial. There's urgency but also fragile trust — people have seen leaders come and go.",
    yourJob:
      "Stabilize, diagnose honestly, and move quickly. Your job is to change the trajectory — that requires decisions others avoided.",
    avoid:
      "Avoid false reassurance and slow timelines. People need to see you act, not just listen. Indecision compounds the damage.",
  },
  realignment: {
    mood:
      "The team is proud of past success and may resist change — they don't see the need for it.",
    yourJob:
      "Acknowledge what works, then make the case for why the strategy must evolve. You need to challenge without destroying.",
    avoid:
      "Avoid criticizing past leadership or treating past wins as irrelevant. You'll lose your allies and harden your skeptics.",
  },
  sustaining: {
    mood:
      "The team is high-performing and collaborative. They don't need rescuing — they need leadership that matches their level.",
    yourJob:
      "Understand deeply before changing anything. Your biggest contribution may be enabling the team to do what they're already capable of.",
    avoid:
      "Avoid coming in with a change agenda. The biggest risk is disrupting what works in an attempt to make your mark.",
  },
};

export default function MandateSituation() {
  const { transition, updateTransition } = useMandateStore();

  const [selectedType, setSelectedType] = useState<STaRSSituation | null>(
    transition?.situationType ?? null
  );
  const [notes, setNotes] = useState(transition?.situationNotes ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (transition) {
      setSelectedType(transition.situationType ?? null);
      setNotes(transition.situationNotes ?? '');
    }
  }, [transition]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateTransition({
        situationType: selectedType ?? undefined,
        situationNotes: notes.trim() || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  const intel = selectedType ? STARS_INTELLIGENCE[selectedType] : null;

  return (
    <div>
      <PageHeader
        icon={<Compass className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Situation Assessment"
        subtitle="STaRS — the type of transition you're navigating"
        actions={
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm"
          >
            {saved ? 'Saved!' : saving ? 'Saving...' : 'Update situation'}
          </button>
        }
      />

      {/* STaRS type selector */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {SITUATION_OPTIONS.map((opt) => (
          <button
            key={opt.type}
            onClick={() => setSelectedType(opt.type)}
            className={`text-left p-4  transition-all ${
              selectedType === opt.type
                ? 'border-2 border-primary-500 bg-primary-50'
                : 'border border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)]'
            }`}
          >
            <div className="font-semibold text-[var(--text-pri)] text-sm mb-0.5">{opt.label}</div>
            <div className="text-xs text-[var(--text-ter)]">{opt.description}</div>
          </button>
        ))}
      </div>

      {/* Situation notes */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
          Situation notes{' '}
          <span className="text-[var(--text-ter)] font-normal">(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          placeholder="What do you know about this situation so far?"
        />
      </div>

      {/* STaRS intelligence */}
      {intel && selectedType && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-4">
            {SITUATION_OPTIONS.find((o) => o.type === selectedType)?.label} — Strategic implications
          </h2>
          <div className="space-y-4">
            <div>
              <div className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wider mb-1">
                The prevailing mood
              </div>
              <p className="text-sm text-[var(--text-sec)]">{intel.mood}</p>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wider mb-1">
                Your job in this situation
              </div>
              <p className="text-sm text-[var(--text-sec)]">{intel.yourJob}</p>
            </div>
            <div>
              <div className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wider mb-1">
                What to avoid
              </div>
              <p className="text-sm text-[var(--text-sec)]">{intel.avoid}</p>
            </div>
          </div>
        </div>
      )}

      {!selectedType && (
        <p className="text-sm text-[var(--text-ter)]">
          Select a situation type above to see the strategic implications.
        </p>
      )}
    </div>
  );
}
